/**
 * Super-Admin-Uebersicht: alle Social-Media-Verbindungen (TikTok/Meta/LinkedIn/Pinterest)
 * aller Workspaces auf einen Blick — nur fuer Administratoren.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Share2, Users, CheckCircle2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLATFORMS = [
  { key: "tiktok", label: "TikTok" },
  { key: "meta", label: "Meta (FB/IG)" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "pinterest", label: "Pinterest" },
];

export default function AdminConnectionsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/social-overview`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ companies: [], total_connections: 0, error: true }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-connections-page">
      <div>
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
          <Share2 className="w-6 h-6 text-[#2563EB]" /> Alle Social-Verbindungen
        </h1>
        <p className="text-sm text-[var(--studio-text-muted)] mt-1">
          Super-Admin-Übersicht: {data.companies.length} Workspaces · {data.total_connections} aktive Verbindungen.
          Mitglieder verbinden ihre Konten selbst unter „Social-Media-Verbindungen" in ihrem Workspace.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.companies.map((c) => (
          <Card key={c.company_id} data-testid={`conn-company-${c.company_id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <span className="truncate">{c.name}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {c.self_registered && (
                    <Badge variant="outline" className="text-[9px] text-violet-600 border-violet-300">Selbst registriert</Badge>
                  )}
                  <Badge variant="outline" className="text-[9px] gap-0.5"><Users className="w-2.5 h-2.5" />{c.members}</Badge>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {PLATFORMS.map((p) => {
                const conns = c.connections.filter((x) => x.platform === p.key);
                return (
                  <div key={p.key} className="flex items-center justify-between text-xs">
                    <span className="text-[var(--studio-text-muted)]">{p.label}</span>
                    {conns.length ? (
                      <span className="flex items-center gap-1 text-emerald-600 font-medium truncate max-w-[60%]"
                        title={conns.map((x) => x.account_name || x.pages?.join(", ") || "verbunden").join(" · ")}>
                        <CheckCircle2 className="w-3 h-3 shrink-0" />
                        {conns[0].account_name || (conns[0].pages?.length ? `${conns[0].pages.length} Seite(n)` : "verbunden")}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </div>
                );
              })}
              {c.connections.some((x) => x.platform === "meta" && x.pages?.length > 0) && (
                <p className="text-[10px] text-[var(--studio-text-muted)] pt-1 border-t border-[var(--studio-border)] truncate"
                  title={c.connections.find((x) => x.platform === "meta")?.pages?.join(", ")}>
                  Meta-Seiten: {c.connections.find((x) => x.platform === "meta")?.pages?.join(", ")}
                </p>
              )}
              <Link to="/social-media" className="inline-flex items-center gap-1 text-[10px] text-[#2563EB] underline pt-1" data-testid={`conn-manage-${c.company_id}`}>
                Verwalten <ExternalLink className="w-2.5 h-2.5" />
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
